import React from "react";
import styled from "styled-components";
import { CustomModal } from "./styles";
import {
  Container,
  Header,
  Title,
  Subtitle,
  ExplainerCard,
  ExplainerTitle,
  ExplainerBody,
  ExplainerList,
  Section,
  SectionTitle,
  Table,
  Pill,
  Footnote,
} from "./ExhibitLayout";

// Hover tooltip on the "Debt to Capital Limit" column header. The
// wrap owns the hover state, the icon is the visual affordance, and
// the tooltip body renders above the header with a downward arrow.
const InfoTooltipWrap = styled.span`
  position: relative;
  display: inline-block;
  margin-left: 6px;
  vertical-align: middle;

  &:hover > div,
  &:focus-within > div {
    visibility: visible;
    opacity: 1;
  }
`;

const InfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid rgba(0, 27, 59, 0.55);
  color: rgba(0, 27, 59, 0.72);
  font-family: 'Mukta Vaani', sans-serif;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  cursor: help;
`;

const InfoTooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 280px;
  max-width: 340px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #D5D9DD;
  border-radius: 8px;
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.12);
  font-family: 'Mukta Vaani', sans-serif;
  font-size: 16px;
  line-height: 22px;
  font-weight: 400;
  color: rgba(0, 27, 59, 0.85);
  text-transform: none;
  letter-spacing: normal;
  text-align: left;
  white-space: normal;
  visibility: hidden;
  opacity: 0;
  transition: opacity 120ms ease-out;
  z-index: 100;
  pointer-events: none;

  &:after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
    width: 10px;
    height: 10px;
    background: #ffffff;
    border-right: 1px solid #D5D9DD;
    border-bottom: 1px solid #D5D9DD;
  }
`;

const getInvestorType = name => {
  if (name === "Prudent Bank") return "Commercial Bank";
  if (name === "Mezz Capital") return "Mezzanine Lender";
  if (name === "Great White Financials") return "Sharky Sparky";
  return "";
};

const getCategory = rating =>
  rating.name === "AAA" || rating.name === "BBB" ? "Investment Grade" : "High Yield";

const getDebtToCapital = (rating, ratings) => {
  const from = (rating.debt_to_capital * 100).toFixed(2);
  const index = ratings.findIndex(i => i.name === rating.name);
  if (index + 1 >= ratings.length) return `${from}% or more`;
  const to = ((ratings[index + 1].debt_to_capital - 0.0001) * 100).toFixed(2);
  return `${from}% to ${to}%`;
};

const ModalExhibit5 = ({ debtClients, ratings, goBack }) => (
  <CustomModal goBack={goBack}>
    <Container>
      <Header>
        <Title>Exhibit 5: Lending Rates & Credit Ratings</Title>
        <Subtitle>How each lender sets its interest rate, and how credit ratings shape the credit spread.</Subtitle>
      </Header>

      <ExplainerCard>
        <ExplainerTitle>How lending rates work</ExplainerTitle>
        <ExplainerBody>
          A lender's interest rate is the sum of three parts:{" "}
          <code>Prime Rate + Credit Spread + Profit Spread</code>.
          <ExplainerList>
            <li><b>Prime Rate</b>: the benchmark rate banks charge their safest business borrowers, set at 3 percentage points above the Federal Reserve's fund rate. So if the Fed's rate is 4%, prime is 7%.</li>
            <li><b>Credit Spread</b>: set by rating agencies based on your credit rating (see the second table). Riskier borrowers get wider spreads.</li>
            <li><b>Profit Spread</b>: each lender's own markup based on how much risk they're willing to take. Shown in basis points (100 bps = 1%).</li>
          </ExplainerList>
        </ExplainerBody>
      </ExplainerCard>

      <Section>
        <SectionTitle>Lenders' Terms & Conditions</SectionTitle>
        <Table>
          <thead>
            <tr>
              <th>Investor Type</th>
              <th>Investor Name</th>
              <th>Loan Term</th>
              <th>
                Debt to Capital Limit
                <InfoTooltipWrap tabIndex={0} aria-label="What is Debt to Capital Limit?">
                  <InfoIcon aria-hidden="true">?</InfoIcon>
                  <InfoTooltip role="tooltip">
                    Caps how much this lender will let you borrow, based on your leverage.
                    Calculated as Total Debt divided by Total Stockholders' Equity.
                    Once your ratio hits their limit, they will not lend you any more.
                  </InfoTooltip>
                </InfoTooltipWrap>
              </th>
              <th>Profit Spread</th>
            </tr>
          </thead>
          <tbody>
            {debtClients && debtClients.map(client => (
              <tr key={client.id}>
                <td>{getInvestorType(client.name)}</td>
                <td><b>{client.name}</b></td>
                <td className="numeric">8 years</td>
                <td className="numeric">{client.lt_debt_to_capital}</td>
                <td className="numeric">{client.lt_company_spread * 10000} bps</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Footnote>* Starting Year 2, Great White Financials can lend, at minimum, $15M, irrespective of your debt to capital ratio.</Footnote>
      </Section>

      <Section>
        <SectionTitle>Corporate Credit Ratings & Spreads</SectionTitle>
        <Table>
          <thead>
            <tr>
              <th>Rating</th>
              <th>Category</th>
              <th>Debt to Capital</th>
              <th>Credit Spread</th>
            </tr>
          </thead>
          <tbody>
            {ratings && ratings.map(rating => {
              const category = getCategory(rating);
              return (
                <tr key={rating.name}>
                  <td><b>{rating.name}</b></td>
                  <td>
                    <Pill variant={category === "Investment Grade" ? "investment" : "high-yield"}>
                      {category}
                    </Pill>
                  </td>
                  <td className="numeric">{getDebtToCapital(rating, ratings)}</td>
                  <td className="numeric">{rating.credit_spread * 10000} bps</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Section>
    </Container>
  </CustomModal>
);

export default ModalExhibit5;



// WEBPACK FOOTER //
// ./src/components/bcase/ModalExhibit5.js